import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { X, Search, Users, Send, CheckSquare, Square } from "lucide-react";

/**
 * Bulk-apply Routine Override modal.
 *
 * Lets a coach take the current Routine Override (blocks + date + base_protocol)
 * and apply it to a multi-selected subset of the team roster in one click.
 */
export default function BulkApplyOverrideModal({ open, onClose, currentPlayerId, blocks, day, baseProtocol = "pre_throw", onApplied }) {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState({});  // {user_id: true}
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/players").then(({ data }) => {
      setRoster(data);
      // Pre-exclude the player you came from (so you don't re-apply to themselves)
      setSelected({});
    }).catch(() => toast.error("Could not load roster"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return roster
      .filter((p) => p.user_id !== currentPlayerId)
      .filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q));
  }, [roster, filter, currentPlayerId]);

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const allSelected = filtered.length > 0 && filtered.every((p) => selected[p.user_id]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      filtered.forEach((p) => { next[p.user_id] = true; });
      setSelected(next);
    }
  };

  const apply = async () => {
    if (selectedIds.length === 0) { toast.error("Pick at least one athlete"); return; }
    if (!blocks || blocks.length === 0) { toast.error("Save at least one block first"); return; }
    if (!confirm(`Apply ${blocks.length} block${blocks.length === 1 ? "" : "s"} to ${selectedIds.length} athlete${selectedIds.length === 1 ? "" : "s"} on ${day}?`)) return;
    setApplying(true);
    try {
      // Strip tmp_ block_ids — backend assigns fresh ones per athlete.
      const cleanBlocks = blocks.map(({ block_id, ...rest }) => ({ ...rest }));
      const { data } = await api.post("/players/routine-override/bulk-apply", {
        date: day,
        base_protocol: baseProtocol,
        blocks: cleanBlocks,
        player_user_ids: selectedIds,
      });
      toast.success(`Applied to ${data.applied_count} athlete${data.applied_count === 1 ? "" : "s"}`);
      onApplied?.(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setApplying(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-8"
      onClick={onClose} data-testid="bulk-apply-overlay">
      <div className="w-full max-w-2xl max-h-full overflow-hidden rounded-2xl border border-[var(--ink-border-strong)] flex flex-col"
        style={{ backgroundColor: "var(--ink-2)" }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-[var(--ink-border)] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase flex items-center gap-1.5" style={{ color: "var(--brand-secondary)" }}>
              <Users className="h-3 w-3" /> Bulk Apply Routine Override
            </div>
            <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1">
              Apply this routine to multiple athletes
            </h2>
            <p className="text-xs text-white/55 mt-1">
              <b className="text-white">{blocks?.length || 0}</b> block{(blocks?.length || 0) === 1 ? "" : "s"} · target date <b className="text-white">{day}</b>
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="bulk-apply-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filter + select-all */}
        <div className="p-4 border-b border-[var(--ink-border)] flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter athletes by name or email…"
              className="pl-9 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="bulk-apply-filter" />
          </div>
          <Button onClick={toggleAll} variant="outline"
            className="border-white/15 text-white bg-white/5 hover:bg-white/10 whitespace-nowrap"
            data-testid="bulk-apply-toggle-all">
            {allSelected ? <CheckSquare className="h-4 w-4 mr-1.5" /> : <Square className="h-4 w-4 mr-1.5" />}
            {allSelected ? "Clear all" : "Select all"}
          </Button>
        </div>

        {/* Roster list */}
        <div className="flex-1 overflow-y-auto p-4" data-testid="bulk-apply-roster">
          {loading && <div className="text-sm text-white/50 text-center py-8">Loading roster…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-sm text-white/40 text-center py-8 italic">No teammates match this filter.</div>
          )}
          <div className="space-y-1">
            {filtered.map((p) => {
              const on = !!selected[p.user_id];
              return (
                <button key={p.user_id} type="button" onClick={() => toggle(p.user_id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition ${on ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                  data-testid={`bulk-apply-player-${p.user_id}`}>
                  {on ? <CheckSquare className="h-4 w-4 text-cyan-300 flex-shrink-0" /> : <Square className="h-4 w-4 text-white/30 flex-shrink-0" />}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-semibold text-white text-sm truncate">{p.name || p.email}</div>
                    <div className="text-[10px] text-white/45">{p.position || "—"} · {p.year || "—"}</div>
                  </div>
                  {p.status && (
                    <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      p.status === "critical" ? "bg-red-500/15 text-red-300" :
                      p.status === "hold"     ? "bg-amber-500/15 text-amber-300" :
                                                "bg-emerald-500/15 text-emerald-300"
                    }`}>{p.status}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--ink-border)] flex items-center justify-between gap-3">
          <div className="text-sm text-white/70">
            <b className="text-white">{selectedIds.length}</b> athlete{selectedIds.length === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onClose} variant="outline"
              className="border-white/15 text-white bg-white/5 hover:bg-white/10"
              data-testid="bulk-apply-cancel">Cancel</Button>
            <Button onClick={apply} disabled={applying || selectedIds.length === 0}
              className="text-white font-bold red-glow"
              style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="bulk-apply-confirm">
              <Send className="h-4 w-4 mr-1.5" />
              {applying ? "Applying…" : `Apply to ${selectedIds.length}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
